/**
 * TNK-132 — Store Options sub-page. Consolidates everything that
 * configures how the operator's store presents itself: branding
 * (logo + watermark), listing thresholds (bulk threshold), and the
 * public storefront settings (enable / URL / minimums). Used to live
 * as inline sections on /settings; folded into a single sub-page so
 * the /settings landing is a clean tile grid.
 */
import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Store as StoreIcon, Tag } from "lucide-react";

import { BackButton } from "@/components/BackButton";
import { CardImageUploader } from "@/components/CardImage";
import { FormField } from "@/components/FormField";
import { StorefrontSettings } from "@/components/settings/StorefrontSettings";
import { useApp, type StoreImageKind } from "@/context/AppContext";
import { deleteImage } from "@/features/imageUpload";
import { apiFetch } from "@/lib/apiClient";
import { RequireRole } from "@/lib/auth";

export const Route = createFileRoute("/settings/store-options")({
  component: StoreOptionsPage,
});

function StoreOptionsPage() {
  const { storeImages, setStoreImage, appPreferences, updateAppPreferences } =
    useApp();

  const [bulkThreshold, setBulkThreshold] = useState(
    String(appPreferences.bulkThresholdUsd),
  );

  const commitBulkThreshold = (raw: string) => {
    setBulkThreshold(raw);
    const parsed = parseFloat(raw);
    if (!isNaN(parsed) && parsed >= 0) {
      updateAppPreferences({ bulkThresholdUsd: parsed });
    }
  };

  const handleChange =
    (kind: StoreImageKind, oldRef?: string) => (newRef: string | undefined) => {
      if (oldRef && oldRef !== newRef) void deleteImage(oldRef);
      setStoreImage(kind, newRef ?? null);
    };

  // TNK-206 — storeImages is a localStorage-only cache; if it's empty (cleared
  // cache / new browser) the uploaders show blank even though store_profile has
  // the refs, and a Storefront save would then null them out. Seed from the DB
  // profile when missing so the cache reflects the saved branding.
  useEffect(() => {
    if (storeImages.logo && storeImages.watermark) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await apiFetch("/api/store/profile/me");
        if (!res.ok || cancelled) return;
        const p = (await res.json()) as {
          logoRef?: string | null;
          watermarkRef?: string | null;
        } | null;
        if (!p || cancelled) return;
        if (!storeImages.logo && p.logoRef) setStoreImage("logo", p.logoRef);
        if (!storeImages.watermark && p.watermarkRef)
          setStoreImage("watermark", p.watermarkRef);
      } catch {
        /* best-effort seed; uploaders just stay blank on failure */
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className="flex h-full flex-col">
      <div className="flex items-center justify-between gap-2 pb-3">
        <BackButton fallback={{ to: "/settings" }} label="Settings" />
      </div>

      <div className="border-b border-border pb-3">
        <div className="flex items-center gap-2">
          <StoreIcon className="h-5 w-5 text-muted-foreground" />
          <h1 className="text-2xl font-bold text-foreground">Store options</h1>
        </div>
        <p className="text-sm text-muted-foreground">
          Branding, public storefront, and listing thresholds — settings that
          shape how every card in the catalog gets presented and listed.
        </p>
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto py-4 pr-1">
        <section className="space-y-3 rounded-md border border-border bg-card p-4">
          <div>
            <h2 className="text-sm font-semibold text-foreground">
              Store branding
            </h2>
            <p className="text-[11px] text-muted-foreground">
              Saved images stay in your store account. Auto-attaching them to
              outgoing listings is a follow-up.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <CardImageUploader
              label="Store logo"
              imageRef={storeImages.logo}
              onChange={handleChange("logo", storeImages.logo)}
              fit="square"
              captureFromCamera={false}
              hint="Recommended: 500×500 px square PNG or JPEG (eBay store logo minimum is 300×300). Used on receipts and as a branding slot on listings."
            />
            <CardImageUploader
              label="Watermark"
              imageRef={storeImages.watermark}
              onChange={handleChange("watermark", storeImages.watermark)}
              fit="free"
              captureFromCamera={false}
              hint="Recommended: 1200×1200 px transparent PNG at full opacity — the app will apply opacity at composite time (default ~35%). Overlays card photos at Go-Live time once wired up."
            />
          </div>
        </section>

        <RequireRole min="manager">
          <section className="space-y-3 rounded-md border border-border bg-card p-4">
            <div className="flex items-center gap-2">
              <Tag className="h-4 w-4 text-muted-foreground" />
              <div>
                <h2 className="text-sm font-semibold text-foreground">
                  Listing thresholds
                </h2>
                <p className="text-[11px] text-muted-foreground">
                  Cards whose market price clears the bulk threshold earn a
                  single/playset listing recommendation instead of being buried
                  in a bulk lot.
                </p>
              </div>
            </div>

            <div className="max-w-[200px]">
              <FormField
                label="Bulk threshold"
                value={bulkThreshold}
                onChangeText={commitBulkThreshold}
                onBlur={() =>
                  setBulkThreshold(String(appPreferences.bulkThresholdUsd))
                }
                inputMode="decimal"
                suffix="USD"
                placeholder="0.24"
              />
            </div>
          </section>
        </RequireRole>

        <RequireRole min="manager">
          <StorefrontSettings storeImages={storeImages} />
        </RequireRole>
      </div>
    </section>
  );
}
